'use client';

import { useState, useEffect } from 'react';

import { useRouter } from 'next/navigation';

import { addLocalePath } from '@/utils/urlBuilder/utils';

export function useNavigation({
  locale,
  activeStep,
  activePhoto,
  setActiveStep,
  setActivePhoto,
  steps,
  guide,
  siblings,
}) {
  const router = useRouter();
  const [hasPrevSlideOrStep, setHasPrevSlideOrStep] = useState(true);
  const [hasNextSlideOrStep, setHasNextSlideOrStep] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  function findSibling(arr, id, direction = 'next') {
    // For direct direction, find the item where default_id matches the provided id
    if (direction === 'direct') {
      return arr.find(item => item.default_id === id);
    }

    // For previous/next directions, first find the index based on item.id
    const index = arr.findIndex(item => item.id === id);
    if (index === -1) {
      return;
    }

    const siblingIndex = direction === 'previous' ? index - 1 : index + 1;

    if (siblingIndex < 0 || siblingIndex >= arr.length) {
      return;
    }

    return arr[siblingIndex];
  }

  const goToSlide = newSlug => {
    // Store the current fullscreen state
    if (document.fullscreenElement) {
      sessionStorage.setItem('wasFullscreen', 'true');
    }

    // Perform navigation
    router.push(addLocalePath(locale, `guide/${newSlug}`));
  };

  const fetchGuideAndNavigate = async guideId => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/v1/guides?locale=${locale}&id=${guideId}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch guide');
      }

      const data = await response.json();
      const goToGuide = data?.data?.[0];

      if (goToGuide && goToGuide.slug) {
        goToSlide(`${goToGuide.slug}`);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching guide:', error);
    }
  };

  // Navigation state
  const navigation = {
    chapter: {
      prev: {
        disabled: guide?.meta?.prev_next?.previous === 0,
        hasDirectLink:
          guide?.meta?.prev_next?.previous &&
          guide?.meta?.prev_next?.previous !== 0,
        directTarget: guide?.meta?.prev_next?.previous || null,
        sibling: findSibling(siblings, guide.id, 'previous'),
      },
      next: {
        disabled: guide?.meta?.prev_next?.next === 0,
        hasDirectLink:
          guide?.meta?.prev_next?.next && guide?.meta?.prev_next?.next !== 0,
        directTarget: guide?.meta?.prev_next?.next || null,
        sibling: findSibling(siblings, guide.id, 'next'),
      },
    },
  };

  // Get current step data
  const currentStep = steps?.[activeStep];
  const currentStepGallery = currentStep?.media?.gallery;
  const currentStepVideo = currentStep?.media?.yt_video;

  // Update navigation state
  useEffect(() => {
    if (!steps?.[activeStep]) return;

    setHasPrevSlideOrStep(
      activeStep > 0 || activePhoto > 0 || !navigation.chapter.prev.disabled
    );

    setHasNextSlideOrStep(
      activeStep < steps.length - 1 ||
      (currentStepGallery && activePhoto < currentStepGallery.length - 1) ||
      !navigation.chapter.next.disabled
    );
  }, [activeStep, activePhoto, steps, currentStepGallery, navigation]);

  // Navigation functions
  const prevSlide = () => {
    if (activeStep === 0) {
      // Handle moving to previous chapter
      if (navigation.chapter.prev.hasDirectLink) {
        const prevChapter = findSibling(
          siblings,
          navigation.chapter.prev.directTarget,
          'direct'
        );
        if (prevChapter) {
          goToSlide(`${prevChapter.slug}#last`);
        } else {
          // Not from same guide
          fetchGuideAndNavigate(navigation.chapter.prev.directTarget);
        }
      } else if (
        !navigation.chapter.prev.disabled &&
        navigation.chapter.prev.sibling
      ) {
        goToSlide(`${navigation.chapter.prev.sibling.slug}#last`);
      }
    } else {
      // Move to previous step in current chapter
      const prevStepIndex = activeStep - 1;
      setActiveStep(prevStepIndex);

      if (steps[prevStepIndex]?.media?.gallery) {
        setActivePhoto(steps[prevStepIndex].media.gallery.length - 1);
      }

      router.push(
        `${location.pathname}${steps[prevStepIndex]?.id ? `#${steps[prevStepIndex].id}` : ''
        }`
      );
    }
  };

  const nextSlide = () => {
    if (activeStep === steps.length - 1) {
      // Handle moving to next chapter
      if (navigation.chapter.next.hasDirectLink) {
        const nextChapter = findSibling(
          siblings,
          navigation.chapter.next.directTarget,
          'direct'
        );
        if (nextChapter) {
          goToSlide(`${nextChapter.slug}`);
        } else {
          // Not from same guide
          fetchGuideAndNavigate(navigation.chapter.next.directTarget);
        }
      } else if (
        !navigation.chapter.next.disabled &&
        navigation.chapter.next.sibling
      ) {
        setActiveStep(0);
        setActivePhoto(0);
        goToSlide(navigation.chapter.next.sibling.slug);
      }
    } else {
      // Move to next step in current chapter
      const nextStepIndex = activeStep + 1;
      setActiveStep(nextStepIndex);
      setActivePhoto(0);

      router.push(
        `${location.pathname}${steps[nextStepIndex]?.id ? `#${steps[nextStepIndex].id}` : ''
        }`
      );
    }
  };

  const prevStep = () => {
    if (!currentStepGallery && currentStepVideo) {
      // For video steps, just go to the previous slide
      prevSlide();
    } else if (activePhoto <= 0) {
      // At the first photo of a step
      if (activeStep > 0) {
        // Go to the previous step in the current chapter
        const prevStepIndex = activeStep - 1;
        setActiveStep(prevStepIndex);

        if (steps[prevStepIndex]?.media?.gallery) {
          setActivePhoto(steps[prevStepIndex].media.gallery.length - 1);
        }

        router.push(
          `${location.pathname}${steps[prevStepIndex]?.id ? `#${steps[prevStepIndex].id}` : ''
          }`
        );
      } else {
        // At the first step and first photo, try to navigate to previous chapter
        if (navigation.chapter.prev.hasDirectLink) {
          const prevChapter = findSibling(
            siblings,
            navigation.chapter.prev.directTarget,
            'direct'
          );
          if (prevChapter) {
            goToSlide(`${prevChapter.slug}#last`);
          }
        } else if (
          !navigation.chapter.prev.disabled &&
          navigation.chapter.prev.sibling
        ) {
          goToSlide(`${navigation.chapter.prev.sibling.slug}#last`);
        }
      }
    } else {
      // Just move to the previous photo in the current step
      setActivePhoto(activePhoto - 1);
    }
  };

  const nextStep = () => {
    if (
      (!currentStepGallery && currentStepVideo) ||
      (currentStepGallery && activePhoto >= currentStepGallery.length - 1)
    ) {
      // At the last photo or on a video, move to the next slide
      nextSlide();
    } else {
      // Move to the next photo in the current step
      setActivePhoto(activePhoto + 1);
    }
  };

  return {
    hasPrevSlideOrStep,
    hasNextSlideOrStep,
    currentStep,
    currentStepGallery,
    currentStepVideo,
    prevSlide,
    nextSlide,
    prevStep,
    nextStep,
    findSibling,
    goToSlide,
    isLoading,
  };
}
